import React, { memo, useCallback } from 'react';
import {
  BoundScrollProvider,
  JumpNavigationProvider,
} from '@sweetgreen/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import type { SweetpassHomeScreenProps } from '../../../../navigation/AppNavigation.props';
import type { useSweetpassHomeContentfulData } from '../../hooks';
import {
  useSweetpassChallengesAndRewards,
  useSweetpassHomeBrazeEvents,
  useSweetpassHomeTelemetry,
} from '../../hooks';
import { SweetpassHomeContent } from '../SweetpassHomeContent';
import type { SweetpassHeroTierName } from '../SweetpassHomeHeroStats/SweetpassHomeHeroStats.utils';
import { getSweetpassHeroStatsPaletteForTag } from '../SweetpassHomeHeroStats/SweetpassHomeHeroStats.utils';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassHomeContainer = memo(
  (props: SweetpassHomeContainerProps) => {
    const {
      sweetpassTierName,
      sweetpassHomeActionsRail,
      sweetpassHomeStats,
      contentItems,
      navigation,
    } = props;

    const { track } = useTelemetry();

    const isSweetpassDetailedHeroStatsEnabled = useFeatureFlag(
      'CELS-440-long-sweetpass-detailed-hero-stats-enabled',
    );

    const {
      challenges,
      challengeRewards,
      isFetchingChallenges,
      isFetchingChallengeRewards,
    } = useSweetpassChallengesAndRewards();

    const navigateToBenefits = useCallback(() => {
      navigation.navigate('Modal', { screen: 'SweetpassBenefits' });
      track('sweetpass_home.benefits_cta.tapped');
    }, [navigation, track]);

    useSweetpassHomeTelemetry({
      hasContentfulData:
        Boolean(sweetpassHomeStats) ||
        Boolean(sweetpassHomeActionsRail) ||
        Boolean(contentItems),
      hasSweetpassHomeStats: Boolean(sweetpassHomeStats),
      hasSweetpassHomeActionsRail: Boolean(sweetpassHomeActionsRail),
    });

    useSweetpassHomeBrazeEvents();

    return (
      <BoundScrollProvider>
        <JumpNavigationProvider
          jumpTargetIds={['challenges-rail', 'rewards-rail']}
          withSafeAreaOffset
        >
          <SweetpassHomeContent
            tierLevel={sweetpassTierName}
            detailedHeroStats={isSweetpassDetailedHeroStatsEnabled}
            sweetpassHomeActionsRail={sweetpassHomeActionsRail}
            sweetpassHomeStats={sweetpassHomeStats}
            contentItems={contentItems}
            challenges={challenges}
            isFetchingChallenges={isFetchingChallenges}
            challengeRewards={challengeRewards}
            isFetchingChallengeRewards={isFetchingChallengeRewards}
            palette={getSweetpassHeroStatsPaletteForTag(sweetpassTierName)}
            navigateToBenefits={navigateToBenefits}
          />
        </JumpNavigationProvider>
      </BoundScrollProvider>
    );
  },
);

// ─── Types ─────────────────────────────────────────────────────────────

type SweetpassHomeContainerProps = {
  readonly sweetpassTierName: SweetpassHeroTierName;
} & ReturnType<
  typeof useSweetpassHomeContentfulData
>['sweetpassHomeContentfulData'] &
  SweetpassHomeScreenProps;
